/**
 * Agent Builder — Run view (Phase 2, T2.5 + Run-tab live fix).
 *
 * Subscribe-FIRST live run flow: kick off a run on the async endpoint
 * (POST /api/agent-runs/stream -> {run_id, status:"running"}), immediately open the
 * SSE stream (GET /api/agent-runs/{id}/events) and ANIMATE the @xyflow/react canvas
 * node-by-node as `node_status`/`agent_result`/`awaiting_hitl` frames arrive, then
 * finalize the status strip from GET /api/agent-runs/{id} on `run_finished`/`done`.
 * HITL approve/reject call POST /api/agent-runs/{id}/hitl (with a reviewer note) and
 * re-subscribe so the post-resume trace re-animates. A run-history selector loads a
 * past run (getRun + replay SSE). Clicking a node opens an output/reasoning panel.
 *
 * Falls back to the SYNCHRONOUS POST /api/agent-runs (assembled result) when the
 * async endpoint isn't available (older backend), preserving the prior behavior.
 *
 * The cockpit appears ONLY when VITE_ENABLE_AGENT_BUILDER is on (the route is
 * registered behind that flag in App.tsx). Additive: reuses the existing
 * @xyflow/react foundation + UI primitives + the typed client.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Background,
  BackgroundVariant,
  Controls,
  ReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Play, Loader2, CheckCircle2, XCircle, ShieldAlert, History, X, ChevronRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import Button from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { agentNodeTypes } from '../../components/agent-builder/AgentFlowNode';
import { toFlowEdges, toFlowNodes, graphOf } from '../../components/agent-builder/adapt';
import { agentBuilderApi } from '../../lib/api/agentBuilderApi';
import type {
  AgentRunRecord,
  AgentRunResult,
  AgentWorkflow,
  RunEventFrame,
  RunListItem,
  RunStatus,
  WorkflowGraph,
} from '../../types/agentBuilder';

export interface RunViewProps {
  /** Optional workflow id to pre-select (deep-linked from the Library). */
  workflowId?: string;
  currentUser?: string;
  /** Surfaces the active run id so a sibling TelemetryView can read it. */
  onRunStarted?: (runId: string) => void;
  /** Lifts the collected SSE frames so TelemetryView can populate guardrail KPIs. */
  onEvents?: (events: RunEventFrame[]) => void;
}

interface LiveState {
  nodeStatus: Record<string, RunStatus>;
  confidence: Record<string, number>;
  guardrailFail: Record<string, boolean>;
}

const EMPTY_LIVE: LiveState = { nodeStatus: {}, confidence: {}, guardrailFail: {} };

// Sample task pre-filled per workflow (clearable) — mirrors Agent Studio's SAMPLE_TASK.
// The task is OPTIONAL: a workflow whose inputs are seeded/derived runs without one.
const SAMPLE_TASK: Record<string, string> = {
  'wf-ontology-researcher': 'Suggest an ontology for manufacturing / supply chain',
};
const sampleTaskFor = (wfId: string): string => SAMPLE_TASK[wfId] ?? '';

// Status -> dot color for the step timeline.
const STATUS_DOT: Record<string, string> = {
  pending: '#94a3b8',
  running: '#6366f1',
  success: '#10b981',
  partial: '#f59e0b',
  failed: '#ef4444',
  skipped: '#94a3b8',
  awaiting_hitl: '#f59e0b',
  rejected: '#ef4444',
  cancelled: '#94a3b8',
};

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-neutral-100 py-1.5">
      <div className="truncate font-mono text-[12px] text-neutral-800" title={value}>{value}</div>
      <div className="text-[9px] uppercase tracking-wide text-neutral-400">{label}</div>
    </div>
  );
}

function Metric({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-[9px] uppercase tracking-wide text-neutral-400">{label}</span>
      <span className="text-[12px]">{children}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wide text-neutral-400">{title}</div>
      {children}
    </div>
  );
}

/** Fold the assembled run result's per-node `results` into the live status map. */
function liveFromResult(result: AgentRunResult | null): LiveState {
  if (!result?.results) return EMPTY_LIVE;
  const nodeStatus: Record<string, RunStatus> = {};
  const confidence: Record<string, number> = {};
  for (const [nodeId, r] of Object.entries(result.results)) {
    nodeStatus[nodeId] = r.status;
    if (typeof r.confidence === 'number') confidence[nodeId] = r.confidence;
  }
  return { nodeStatus, confidence, guardrailFail: {} };
}

/** Fold a persisted run record's steps into the live status map (history replay). */
function liveFromRecord(rec: AgentRunRecord | null): LiveState {
  if (!rec?.steps) return EMPTY_LIVE;
  const nodeStatus: Record<string, RunStatus> = {};
  const confidence: Record<string, number> = {};
  const guardrailFail: Record<string, boolean> = {};
  for (const s of rec.steps) {
    nodeStatus[s.nodeId] = s.status;
    if (typeof s.confidence === 'number') confidence[s.nodeId] = s.confidence;
    if (Array.isArray(s.guardrails)) guardrailFail[s.nodeId] = s.guardrails.some((g) => g.passed === false);
  }
  return { nodeStatus, confidence, guardrailFail };
}

export const RunView: React.FC<RunViewProps> = ({ workflowId, currentUser, onRunStarted, onEvents }) => {
  const [workflows, setWorkflows] = useState<AgentWorkflow[]>([]);
  const [selectedWf, setSelectedWf] = useState<string>(workflowId ?? '');
  const [task, setTask] = useState('');
  const [graph, setGraph] = useState<WorkflowGraph | null>(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<AgentRunResult | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [runStatus, setRunStatus] = useState<RunStatus | null>(null);
  const [live, setLive] = useState<LiveState>(EMPTY_LIVE);
  const [events, setEvents] = useState<RunEventFrame[]>([]);
  const [hitlNote, setHitlNote] = useState('');
  const [record, setRecord] = useState<AgentRunRecord | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [history, setHistory] = useState<RunListItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  // The HITL-suspended node id captured straight from the live SSE `awaiting_hitl`
  // frame — so the Approve/Reject panel appears the instant a run suspends, without
  // waiting on (or racing) the persisted record fetch.
  const [liveSuspended, setLiveSuspended] = useState<string | null>(null);
  const [showLog, setShowLog] = useState(false);
  const unsubRef = useRef<null | (() => void)>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    // Read the workflow list through the shared React Query cache
    // (staleTime: Infinity) so it isn't re-fetched on revisit.
    queryClient
      .fetchQuery({ queryKey: ['agent-workflows'], queryFn: () => agentBuilderApi.listWorkflows(), staleTime: Infinity })
      .then((rows) => {
        setWorkflows(rows);
        const initial = workflowId || rows[0]?.workflowId || '';
        setSelectedWf(initial);
        setTask((prev) => prev || sampleTaskFor(initial));
      })
      .catch(() => toast.error('Failed to load workflows'));
  }, [workflowId, queryClient]);

  // Pre-fill the sample task when the user picks a different workflow (Agent Studio parity).
  const onSelectWorkflow = useCallback((id: string) => {
    setSelectedWf(id);
    setTask(sampleTaskFor(id));
  }, []);

  // Keep the canvas graph in sync with the selected workflow.
  useEffect(() => {
    const wf = workflows.find((w) => w.workflowId === selectedWf);
    setGraph(wf ? graphOf(wf) : null);
  }, [workflows, selectedWf]);

  useEffect(() => () => unsubRef.current?.(), []);

  // Lift events to the page (for TelemetryView guardrail KPIs).
  useEffect(() => {
    onEvents?.(events);
  }, [events, onEvents]);

  /** Pull the latest persisted record to finalize the status strip + node panel. */
  const finalize = useCallback(
    (id: string) => {
      if (typeof agentBuilderApi.getRun !== 'function') return;
      agentBuilderApi
        .getRun(id)
        .then((rec) => {
          setRecord(rec);
          setRunStatus(rec.status);
          // Prefer the assembled result if the record carries one; else fold steps.
          setLive((prev) => {
            const folded = liveFromRecord(rec);
            return {
              nodeStatus: { ...prev.nodeStatus, ...folded.nodeStatus },
              confidence: { ...prev.confidence, ...folded.confidence },
              guardrailFail: { ...prev.guardrailFail, ...folded.guardrailFail },
            };
          });
          if (rec.result) setResult(rec.result);
        })
        .catch(() => undefined);
    },
    []
  );

  const applyFrame = useCallback(
    (frame: RunEventFrame) => {
      setEvents((prev) => [...prev, frame]);
      // run_finished / done frames carry the run status; finalize from the record.
      if (frame.type === 'run_finished' || frame.type === 'done') {
        const id = (frame.run_id as string) || runId;
        if (frame.status) setRunStatus(frame.status as RunStatus);
        if (id) finalize(id);
        return;
      }
      if (!frame.node_id) return;
      const nodeId = frame.node_id;
      // Capture the suspended node straight from the live frame so the HITL panel
      // appears immediately (independent of the persisted-record fetch).
      if (frame.type === 'awaiting_hitl' || frame.status === 'awaiting_hitl') {
        setLiveSuspended(nodeId);
      }
      setLive((prev) => {
        const next: LiveState = {
          nodeStatus: { ...prev.nodeStatus },
          confidence: { ...prev.confidence },
          guardrailFail: { ...prev.guardrailFail },
        };
        if (frame.status) next.nodeStatus[nodeId] = frame.status;
        else if (frame.type === 'node_status') next.nodeStatus[nodeId] = 'running';
        else if (frame.type === 'agent_result') next.nodeStatus[nodeId] = 'success';
        else if (frame.type === 'awaiting_hitl') next.nodeStatus[nodeId] = 'awaiting_hitl';
        if (typeof frame.confidence === 'number') next.confidence[nodeId] = frame.confidence;
        if (Array.isArray(frame.guardrails)) {
          next.guardrailFail[nodeId] = frame.guardrails.some((g) => g.passed === false);
        }
        return next;
      });
    },
    [runId, finalize]
  );

  const subscribe = useCallback(
    (id: string, resetEvents = true) => {
      unsubRef.current?.();
      if (resetEvents) setEvents([]);
      unsubRef.current = agentBuilderApi.subscribeRunEvents(
        id,
        applyFrame,
        () => finalize(id),
        () => undefined
      );
    },
    [applyFrame, finalize]
  );

  const handleStart = useCallback(async () => {
    if (!selectedWf) {
      toast.error('Pick a workflow to run');
      return;
    }
    setRunning(true);
    setResult(null);
    setRecord(null);
    setLive(EMPTY_LIVE);
    setRunId(null);
    setRunStatus(null);
    setEvents([]);
    setSelectedNode(null);
    setLiveSuspended(null);
    const req = { workflowId: selectedWf, task };
    try {
      // Subscribe-FIRST live path: async kickoff returns a run id immediately, then
      // we tail the SSE stream so the canvas animates node-by-node.
      const startAsync = (agentBuilderApi as { startRunAsync?: typeof agentBuilderApi.startRunAsync }).startRunAsync;
      if (typeof startAsync === 'function') {
        const ack = await startAsync(req, currentUser);
        if (ack?.run_id) {
          setRunId(ack.run_id);
          setRunStatus((ack.status as RunStatus) || 'running');
          onRunStarted?.(ack.run_id);
          subscribe(ack.run_id);
          toast.message('Run started');
          return;
        }
      }
      // Fallback: synchronous run returns the assembled result.
      const res = await agentBuilderApi.startRun(req, currentUser);
      setResult(res);
      setRunStatus(res.status);
      setLive(liveFromResult(res));
      if (res.run_id) {
        setRunId(res.run_id);
        onRunStarted?.(res.run_id);
        subscribe(res.run_id);
        finalize(res.run_id);
      }
      if (res.status === 'awaiting_hitl') toast.warning('Run awaiting human review');
      else if (res.status === 'success') toast.success('Run completed');
      else toast.message(`Run ${res.status}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to start run');
      // eslint-disable-next-line no-console
      console.error('startRun failed', err);
    } finally {
      setRunning(false);
    }
  }, [selectedWf, task, currentUser, onRunStarted, subscribe, finalize]);

  const suspendedNode = result?.suspended_node || record?.suspendedNode || liveSuspended || null;

  const handleHitl = useCallback(
    async (approved: boolean) => {
      if (!runId || !suspendedNode) return;
      setRunning(true);
      try {
        const res = await agentBuilderApi.decideHitl(
          runId,
          { nodeId: suspendedNode, approved, note: hitlNote },
          currentUser
        );
        setResult(res);
        setRunStatus(res.status);
        setLive(liveFromResult(res));
        setHitlNote('');
        // Clear the live-suspended marker once the run is no longer paused.
        if (res.status !== 'awaiting_hitl') setLiveSuspended(null);
        // Re-subscribe so the post-resume trace re-animates (live or replay).
        subscribe(runId);
        finalize(runId);
        toast.success(approved ? 'Approved — run resumed' : 'Rejected');
      } catch (err: any) {
        toast.error(err?.response?.data?.detail || 'Failed to submit decision');
        // eslint-disable-next-line no-console
        console.error('decideHitl failed', err);
      } finally {
        setRunning(false);
      }
    },
    [runId, suspendedNode, hitlNote, currentUser, subscribe, finalize]
  );

  const openHistory = useCallback(() => {
    const list = (agentBuilderApi as { listRuns?: typeof agentBuilderApi.listRuns }).listRuns;
    if (typeof list !== 'function') {
      toast.message('Run history unavailable');
      return;
    }
    setShowHistory(true);
    list(selectedWf ? { workflowId: selectedWf, limit: 25 } : { limit: 25 })
      .then(setHistory)
      .catch(() => toast.error('Failed to load run history'));
  }, [selectedWf]);

  const loadPastRun = useCallback(
    (id: string) => {
      setShowHistory(false);
      setRunId(id);
      onRunStarted?.(id);
      setResult(null);
      setLive(EMPTY_LIVE);
      setSelectedNode(null);
      setLiveSuspended(null);
      finalize(id);
      // Replay the past run's persisted events to re-animate the trace.
      subscribe(id);
    },
    [onRunStarted, finalize, subscribe]
  );

  const status = runStatus ?? result?.status ?? null;
  const awaitingHitl = status === 'awaiting_hitl' && !!suspendedNode;

  // Output nodes aren't executed by the engine (they're terminal/assembly), so they
  // never get a step/frame. Paint them 'success' once the run succeeds so the canvas
  // reads as complete — matching Agent Studio, which marks output nodes done.
  const paintedStatus = useMemo(() => {
    const ns: Record<string, RunStatus> = { ...live.nodeStatus };
    if (status === 'success') {
      for (const n of graph?.nodes ?? []) {
        if (n.kind === 'output' && !ns[n.id]) ns[n.id] = 'success';
      }
    }
    return ns;
  }, [live.nodeStatus, status, graph]);

  const rfNodes = useMemo(
    () =>
      graph
        ? toFlowNodes(graph, {
            nodeStatus: paintedStatus,
            confidence: live.confidence,
            guardrailFail: live.guardrailFail,
            selectedId: selectedNode,
          })
        : [],
    [graph, paintedStatus, live.confidence, live.guardrailFail, selectedNode]
  );
  const rfEdges = useMemo(() => (graph ? toFlowEdges(graph, live) : []), [graph, live]);

  const onNodeClick = useCallback((_: unknown, node: { id: string }) => {
    setSelectedNode((prev) => (prev === node.id ? null : node.id));
  }, []);

  // Friendly label + the guardrail that blocked the suspended node (for the HITL
  // panel). Reason comes from the persisted step or, live, from the collected frames.
  const suspendedLabel = useMemo(() => {
    if (!suspendedNode) return '';
    return (
      graph?.nodes.find((n) => n.id === suspendedNode)?.label ||
      record?.steps?.find((s) => s.nodeId === suspendedNode)?.agentName ||
      suspendedNode
    );
  }, [suspendedNode, graph, record]);
  const hitlReason = useMemo(() => {
    if (!suspendedNode) return null;
    const step = record?.steps?.find((s) => s.nodeId === suspendedNode);
    let g = step?.guardrails?.find((x) => x.passed === false);
    if (!g) {
      for (const e of events) {
        if (e.node_id === suspendedNode && Array.isArray(e.guardrails)) {
          const f = e.guardrails.find((x) => x.passed === false);
          if (f) { g = f; break; }
        }
      }
    }
    return g ? { name: g.name, detail: g.detail ?? '' } : null;
  }, [suspendedNode, record, events]);
  // Count of nodes currently awaiting review — Approve resolves ALL of them.
  const awaitingCount = useMemo(
    () => Object.values(live.nodeStatus).filter((s) => s === 'awaiting_hitl').length,
    [live.nodeStatus]
  );

  // The clicked node's step (from the persisted record) for the output panel.
  const selectedStep = useMemo(
    () => (selectedNode ? record?.steps?.find((s) => s.nodeId === selectedNode) ?? null : null),
    [selectedNode, record]
  );

  // Step timeline + "agents succeeded/total" metric (Agent Studio parity).
  const steps = useMemo(() => record?.steps ?? [], [record]);
  const agentsTotal = useMemo(
    () => (graph?.nodes ?? []).filter((n) => n.kind !== 'output').length,
    [graph]
  );
  const agentsDone = useMemo(
    () => new Set(steps.filter((s) => s.status === 'success').map((s) => s.nodeId)).size,
    [steps]
  );

  return (
    <div className="flex h-full flex-col overflow-hidden" data-testid="agent-run-view">
      {/* Control bar */}
      <div className="flex shrink-0 flex-wrap items-end gap-3 border-b border-neutral-200 bg-neutral-50 p-4">
        <div className="flex min-w-[200px] flex-col">
          <label className="mb-1 text-[11px] uppercase tracking-wide text-neutral-500">Workflow</label>
          <select
            value={selectedWf}
            onChange={(e) => onSelectWorkflow(e.target.value)}
            disabled={running}
            className="rounded-lg border border-neutral-300 px-2.5 py-2 text-[13px]"
            aria-label="Run workflow"
          >
            <option value="">Select a workflow…</option>
            {workflows.map((w) => (
              <option key={w.workflowId} value={w.workflowId}>{w.name}</option>
            ))}
          </select>
        </div>
        <div className="flex min-w-[240px] flex-1 flex-col">
          <label className="mb-1 text-[11px] uppercase tracking-wide text-neutral-500">Task <span className="normal-case text-neutral-400">(optional)</span></label>
          <input
            value={task}
            onChange={(e) => setTask(e.target.value)}
            disabled={running}
            placeholder="Optional — describe the task…"
            className="rounded-lg border border-neutral-300 px-2.5 py-2 text-[13px]"
            aria-label="Run task"
          />
        </div>
        <Button variant="primary" onClick={handleStart} disabled={running || !selectedWf}>
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run
        </Button>
        <Button variant="outline" onClick={openHistory} disabled={running} aria-label="History">
          <History className="h-4 w-4" /> History
        </Button>
      </div>

      {/* Status strip — labeled metric cells (Agent Studio parity) */}
      {(status || runId) && (
        <div className="flex flex-wrap items-center gap-5 border-b border-neutral-200 bg-white px-5 py-2.5 text-[12px]" data-testid="run-status-strip">
          <Metric label="status">
            <span className="inline-flex items-center gap-1.5 font-medium" style={{ color: STATUS_DOT[status ?? ''] ?? '#64748b' }}>
              <span className="h-2 w-2 rounded-full" style={{ background: STATUS_DOT[status ?? ''] ?? '#94a3b8' }} />
              {status ?? '—'}
            </span>
          </Metric>
          <Metric label="agents">
            <span className="font-medium text-neutral-700">{agentsDone}/{agentsTotal}</span>
          </Metric>
          <Metric label="tokens">
            <span className="font-mono text-neutral-700">{(record?.totalTokens ?? result?.total_tokens ?? 0).toLocaleString()}</span>
          </Metric>
          <Metric label="cost">
            <span className="font-mono text-neutral-700">${(record?.totalCostUsd ?? result?.total_cost_usd ?? 0).toFixed(4)}</span>
          </Metric>
          <Metric label="confidence">
            <span className="font-mono text-neutral-700">{(record?.overallConfidence ?? result?.overall_confidence)?.toFixed?.(2) ?? '—'}</span>
          </Metric>
          {runId && <span className="ml-auto font-mono text-[11px] text-neutral-400">{runId}</span>}
        </div>
      )}

      {/* Canvas + persistent right rail (Agent Studio parity) */}
      <div className="relative flex min-h-0 flex-1">
        <div className="relative min-h-0 flex-1">
          {graph ? (
            <>
              <ReactFlowProvider>
                <ReactFlow
                  nodes={rfNodes}
                  edges={rfEdges}
                  nodeTypes={agentNodeTypes}
                  fitView
                  fitViewOptions={{ padding: 0.25 }}
                  proOptions={{ hideAttribution: true }}
                  minZoom={0.4}
                  nodesDraggable={false}
                  nodesConnectable={false}
                  onNodeClick={onNodeClick}
                >
                  <Background variant={BackgroundVariant.Dots} gap={22} size={1} color="#e2e8f0" />
                  <Controls showInteractive={false} />
                </ReactFlow>
              </ReactFlowProvider>
              {/* Empty-state overlay before the first run — a soft blur/dim over the
                  canvas so the prompt reads cleanly without clashing with the nodes. */}
              {!runId && !running && (
                <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center" data-testid="run-empty-overlay">
                  <div className="absolute inset-0 bg-white/55 backdrop-blur-sm" />
                  <div className="relative rounded-xl border border-neutral-200 bg-white/90 px-6 py-4 text-center text-[13px] text-neutral-500 shadow-sm backdrop-blur">
                    <div className="mb-1 text-2xl opacity-40">▶</div>
                    Press <span className="font-medium text-neutral-700">Run</span> to execute and watch the
                    agents light up.
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex h-full items-center justify-center text-neutral-400" data-testid="run-no-graph">
              Select a workflow to view its graph.
            </div>
          )}
        </div>

        {/* Right rail: HITL gate (when awaiting) → Step Inspector → Step Timeline */}
        <div className="flex w-[360px] shrink-0 flex-col overflow-hidden border-l border-neutral-200 bg-white" data-testid="run-rail">
          {/* HITL approve/reject (Agent Studio parity: top of the rail) */}
          {awaitingHitl && (
            <div className="shrink-0 border-b border-amber-300 bg-amber-50 p-4" data-testid="hitl-panel">
              <div className="mb-1 flex items-center gap-2 text-[13px] font-semibold text-amber-700">
                <ShieldAlert className="h-4 w-4" /> Human-in-the-loop required
              </div>
              <p className="mb-2 text-[12px] leading-relaxed text-amber-800">
                <span className="font-medium text-amber-900">{suspendedLabel}</span>
                {hitlReason ? (
                  <>
                    {' '}is blocked by <span className="font-medium text-amber-900">{hitlReason.name}</span>
                    {hitlReason.detail ? `: ${hitlReason.detail}` : ''}
                  </>
                ) : (
                  ' is awaiting approval'
                )}
                {awaitingCount > 1 && (
                  <span className="text-amber-600"> · +{awaitingCount - 1} more pending (Approve resolves all)</span>
                )}
              </p>
              <input
                value={hitlNote}
                onChange={(e) => setHitlNote(e.target.value)}
                disabled={running}
                placeholder="Reviewer note (optional)…"
                className="mb-2 w-full rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-[12px]"
                aria-label="HITL note"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => handleHitl(true)}
                  disabled={running}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-[12px] font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
                  aria-label="Approve"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                </button>
                <button
                  onClick={() => handleHitl(false)}
                  disabled={running}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[12px] font-medium text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50"
                  aria-label="Reject"
                >
                  <XCircle className="h-3.5 w-3.5" /> Reject
                </button>
              </div>
            </div>
          )}
          {/* Step inspector */}
          <div className="shrink-0 overflow-y-auto border-b border-neutral-200 p-4" style={{ maxHeight: '58%' }} data-testid="step-inspector">
            {selectedStep ? (
              <div className="space-y-3 text-[12px]">
                <div className="flex items-center justify-between">
                  <span className="truncate font-semibold text-neutral-800">{selectedStep.agentName || selectedNode}</span>
                  <Badge
                    variant={selectedStep.status === 'success' ? 'success' : selectedStep.status === 'failed' || selectedStep.status === 'rejected' ? 'error' : 'warning'}
                    size="sm"
                  >
                    {selectedStep.status}
                  </Badge>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <Stat label="conf" value={selectedStep.confidence > 0 ? selectedStep.confidence.toFixed(2) : '—'} />
                  <Stat label="model" value={selectedStep.model || '—'} />
                  <Stat label="tokens" value={(selectedStep.tokens ?? 0).toLocaleString()} />
                  <Stat label="dur" value={`${(selectedStep.durationSeconds ?? 0).toFixed(1)}s`} />
                </div>
                {selectedStep.reasoning && (
                  <Section title="Reasoning">
                    <p className="leading-relaxed text-neutral-700">{selectedStep.reasoning}</p>
                  </Section>
                )}
                {Array.isArray(selectedStep.guardrails) && selectedStep.guardrails.length > 0 && (
                  <Section title="Guardrails">
                    <ul className="space-y-1">
                      {selectedStep.guardrails.map((g, i) => (
                        <li key={`${g.name}-${i}`} className={g.passed ? 'text-emerald-600' : 'text-red-600'}>
                          {g.passed ? '✓' : '✗'} {g.name} <span className="text-neutral-400">({g.kind})</span>
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}
                <Section title="Output">
                  <pre className="max-h-60 overflow-auto rounded bg-neutral-900 p-2 text-[11px] text-neutral-100">
                    {JSON.stringify(selectedStep.data ?? {}, null, 2)}
                  </pre>
                </Section>
              </div>
            ) : (
              <div className="text-[12px] text-neutral-400">
                Select a step below (or a node on the canvas) to inspect its confidence, model, tokens,
                duration, output and guardrail verdicts.
              </div>
            )}
          </div>
          {/* Step timeline */}
          <div className="min-h-0 flex-1 overflow-y-auto" data-testid="step-timeline">
            <div className="px-4 py-2 text-[11px] uppercase tracking-wide text-neutral-400">Step timeline</div>
            <div className="flex flex-col gap-1 px-2 pb-3">
              {steps.map((s, i) => (
                <button
                  key={`${s.nodeId}-${i}`}
                  onClick={() => setSelectedNode(s.nodeId)}
                  className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors ${
                    selectedNode === s.nodeId ? 'border-secondary-300 bg-secondary-50' : 'border-transparent hover:bg-neutral-50'
                  }`}
                >
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: STATUS_DOT[s.status] ?? '#94a3b8' }} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-[12.5px] text-neutral-800">{s.agentName || s.nodeId}</span>
                      {s.iteration > 1 && (
                        <span className="rounded bg-neutral-100 px-1 text-[9px] text-emerald-600">↻{s.iteration}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-neutral-400">
                      {(s.tokens ?? 0).toLocaleString()} tok · {(s.durationSeconds ?? 0).toFixed(1)}s
                    </div>
                  </div>
                  <span className="font-mono text-[10px] text-neutral-400">
                    {s.confidence > 0 ? s.confidence.toFixed(2) : '—'}
                  </span>
                </button>
              ))}
              {steps.length === 0 && (
                <div className="px-3 py-2 text-[12px] text-neutral-400">No steps yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Run-history selector */}
      {showHistory && (
        <div className="max-h-56 overflow-y-auto border-t border-neutral-200 bg-white p-3" data-testid="run-history">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-semibold text-neutral-700">Recent runs</span>
            <button onClick={() => setShowHistory(false)} aria-label="Close history" className="text-neutral-400 hover:text-neutral-700">
              <X className="h-4 w-4" />
            </button>
          </div>
          {history.length === 0 ? (
            <div className="py-4 text-center text-[12px] text-neutral-400">No runs yet.</div>
          ) : (
            <ul className="space-y-1">
              {history.map((h) => (
                <li key={h.runId}>
                  <button
                    onClick={() => loadPastRun(h.runId)}
                    className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-[12px] hover:bg-neutral-50"
                  >
                    <span className="truncate">
                      <span className="font-mono text-[11px] text-neutral-400">{h.runId}</span>{' '}
                      <span className="text-neutral-600">{h.task}</span>
                    </span>
                    <Badge
                      variant={h.status === 'success' ? 'success' : h.status === 'failed' || h.status === 'rejected' ? 'error' : 'warning'}
                      size="sm"
                    >
                      {h.status}
                    </Badge>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Event log — collapsed by default (developer detail; off by default for a clean view) */}
      {events.length > 0 && (
        <div className="shrink-0 border-t border-neutral-200 bg-neutral-50" data-testid="run-event-log">
          <button
            onClick={() => setShowLog((s) => !s)}
            className="flex w-full items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-neutral-500 hover:text-neutral-800"
            aria-expanded={showLog}
          >
            {showLog ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Event log <span className="text-neutral-400">({events.length})</span>
          </button>
          {showLog && (
            <ul className="max-h-40 space-y-0.5 overflow-y-auto px-3 pb-3 font-mono text-[11px] text-neutral-600">
              {events.map((e, i) => (
                <li key={`${e.type}-${e.seq ?? i}`}>
                  <span className="text-neutral-400">#{e.seq ?? i}</span>{' '}
                  <span className="font-semibold text-secondary-700">{e.type}</span>
                  {e.node_id ? ` · ${e.node_id}` : ''}
                  {e.status ? ` → ${e.status}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default RunView;
